import { isApiError } from "@colorglo/contracts/client";
import { QueryClient } from "@tanstack/react-query";

/**
 * The query client.
 *
 * ── Why the retry rule is not the default ─────────────────────────────────────
 *
 * TanStack retries three times on any failure. For this API that is wrong twice over: a 403
 * is not going to become a 200, and a 409 from a booking write is a *normal outcome* that the
 * UI answers with alternatives (docs/API.md) rather than an error to paper over. Retrying
 * either wastes three round trips before showing the user the answer the first one already
 * had — on a workshop's tethered 4G, that is several seconds of a spinner that resolves to a
 * message we could have shown immediately.
 *
 * So: retry network and 5xx failures, and nothing a 4xx tells us on purpose.
 *
 * ── Why the calendar is not `refetchOnWindowFocus` ────────────────────────────
 *
 * DESIGN.md §10: "Auto-refresh that moves content under the pointer" is a thing not to build.
 * A technician who alt-tabs back mid-drag must not have the grid re-lay itself underneath
 * them. Freshness is handled explicitly instead — invalidation after a write, and the
 * "3 nya bokningar — uppdatera" affordance the design asks for.
 */

/** 4xx means the server decided; asking again does not change its mind. */
function isFinalAnswer(error: unknown): boolean {
  if (!isApiError(error)) return false;
  const code = error.error.code;
  return (
    code === "FORBIDDEN" ||
    code === "UNAUTHENTICATED" ||
    code === "NOT_FOUND" ||
    code === "VALIDATION_FAILED" ||
    code === "SLOT_UNAVAILABLE" ||
    code === "INVALID_STATUS_TRANSITION" ||
    code === "OUTSIDE_BOOKING_WINDOW" ||
    code === "TOO_LATE_TO_CANCEL" ||
    code === "STORAGE_UNAVAILABLE" ||
    code === "DAILY_LIMIT_REACHED" ||
    code === "NO_RESOURCE_AVAILABLE" ||
    code === "PRODUCT_UNAVAILABLE" ||
    code === "WORKSHOP_PAUSED" ||
    code === "ADDRESS_INCOMPLETE" ||
    code === "OUTSIDE_WORKING_HOURS"
  );
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (attempt, error) => !isFinalAnswer(error) && attempt < 2,
        refetchOnWindowFocus: false,
        // A workshop's calendar changes when someone in the workshop changes it, and every
        // one of those paths invalidates explicitly. Ten seconds is enough to collapse the
        // burst of refetches a single drag causes without making anything feel stale.
        staleTime: 10_000,
      },
      mutations: {
        // A write that failed has usually failed for a reason. The one exception worth
        // retrying is a dropped connection, and `isFinalAnswer` already lets that through.
        retry: (attempt, error) => !isFinalAnswer(error) && attempt < 1,
      },
    },
  });
}

/**
 * Query keys, in one place.
 *
 * Optimistic updates and invalidation both address queries by key, and a key composed
 * inline at each call site is a cache that silently fails to update — the write invalidates
 * `["calendar", id]` while the read is cached under `["calendar", id, undefined]`, nothing
 * throws, and the calendar just stops refreshing.
 */
export const keys = {
  me: () => ["me"] as const,
  calendar: (franchiseId: string, from: string, to: string) =>
    ["calendar", franchiseId, from, to] as const,
  appointments: (franchiseId: string, from: string, to: string) =>
    ["appointments", franchiseId, from, to] as const,
  /** Every appointment window for this franchise, for invalidation after a write. */
  appointmentsAll: (franchiseId: string) => ["appointments", franchiseId] as const,
  blockers: (franchiseId: string, from: string, to: string) =>
    ["blockers", franchiseId, from, to] as const,
  blockersAll: (franchiseId: string) => ["blockers", franchiseId] as const,
  photos: (franchiseId: string, appointmentId: string) =>
    ["photos", franchiseId, appointmentId] as const,
  notifications: (franchiseId: string, appointmentId: string) =>
    ["notifications", franchiseId, appointmentId] as const,
  /** The staff bell's feed. Franchise-wide; the unread marks inside it are per user. */
  staffNotifications: (franchiseId: string) => ["staff-notifications", franchiseId] as const,
  /** The `/aviseringar` page: the same feed, filtered and paged. */
  staffNotificationHistory: (franchiseId: string, kind: string | null, unreadOnly: boolean) =>
    ["staff-notification-history", franchiseId, kind ?? "", unreadOnly] as const,
  /** Every filter combination, for invalidation after a write. */
  staffNotificationHistoryAll: (franchiseId: string) =>
    ["staff-notification-history", franchiseId] as const,
  /** The signed-in person's sound settings. Not scoped to a franchise — see `0017`. */
  notificationPreferences: () => ["notification-preferences"] as const,
  route: (franchiseId: string, resourceId: string, date: string) =>
    ["route", franchiseId, resourceId, date] as const,
  slots: (franchiseId: string, serviceIds: string, from: string, resourceId: string) =>
    ["slots", franchiseId, serviceIds, from, resourceId] as const,
  /** Every service and resource combination, for invalidation after a write. */
  slotsAll: (franchiseId: string) => ["slots", franchiseId] as const,

  shopCatalogue: (franchiseId: string) => ["shop-catalogue", franchiseId] as const,
  shopProduct: (franchiseId: string, productSlug: string) =>
    ["shop-product", franchiseId, productSlug] as const,
  shopOrders: (franchiseId: string) => ["shop-orders", franchiseId] as const,
  shopOrder: (franchiseId: string, orderId: string) =>
    ["shop-orders", franchiseId, orderId] as const,
  hqShopOrders: (status: string) => ["hq-shop-orders", status] as const,
  hqShopOrdersAll: () => ["hq-shop-orders"] as const,
  hqShopOrder: (orderId: string) => ["hq-shop-order", orderId] as const,
  hqNotifications: () => ["hq-notifications"] as const,
  orderEmails: (orderId: string) => ["order-emails", orderId] as const,
  shopAnalytics: (from: string, to: string, country: string) =>
    ["shop-analytics", from, to, country] as const,
  hqNotificationBell: () => ["hq-notifications", "bell"] as const,
  hqNotificationHistory: (kind: string, unreadOnly: boolean) =>
    ["hq-notifications", "history", kind, unreadOnly] as const,
  adminProducts: () => ["admin-products"] as const,
  adminCategories: () => ["admin-products", "categories"] as const,
  adminProduct: (productId: string) => ["admin-products", productId] as const,
  shopSettings: () => ["shop-settings"] as const,

  openingHours: (franchiseId: string) => ["opening-hours", franchiseId] as const,
  resources: (franchiseId: string) => ["resources", franchiseId] as const,
  reassignment: (franchiseId: string, resourceId: string) =>
    ["resources", franchiseId, resourceId, "reassignment"] as const,
  invitations: (franchiseId: string) => ["invitations", franchiseId] as const,
  staffMember: (franchiseId: string, resourceId: string) =>
    ["resources", franchiseId, resourceId, "staff"] as const,
  staffActivity: (franchiseId: string, resourceId: string) =>
    ["resources", franchiseId, resourceId, "activity"] as const,
  serviceSettings: (franchiseId: string) => ["service-settings", franchiseId] as const,
  bookingRules: (franchiseId: string) => ["booking-rules", franchiseId] as const,
  mobileSettings: (franchiseId: string) => ["mobile-settings", franchiseId] as const,
  messaging: (franchiseId: string) => ["messaging", franchiseId] as const,
  profile: (franchiseId: string) => ["profile", franchiseId] as const,
  notificationRules: (franchiseId: string) => ["notification-rules", franchiseId] as const,
  reminderPreview: (franchiseId: string, template: string, locale: string) =>
    ["reminder-preview", franchiseId, template, locale] as const,
  integrations: (franchiseId: string) => ["integrations", franchiseId] as const,
  icsFeeds: (franchiseId: string) => ["ics-feeds", franchiseId] as const,
  reportSubscriptions: (franchiseId: string) => ["report-subscriptions", franchiseId] as const,
  reportDeliveries: (franchiseId: string) => ["report-deliveries", franchiseId] as const,
  franchiseMetrics: (franchiseId: string, from: string, to: string, metric: string) =>
    ["franchise-metrics", franchiseId, from, to, metric] as const,
} as const;

/**
 * The HQ dashboard's keys (P10).
 *
 * Kept apart from `keys` because they are scoped to a *set* of franchises rather than to one,
 * and because nothing on that surface writes — there is no invalidation story here, only
 * caching. The filter is serialised into the key wholesale so that changing any part of it is
 * a different query rather than a stale hit under the same key.
 */
export const networkKeys = {
  overview: (country: string) => ["network", "overview", country] as const,
  metrics: (filter: NetworkFilterKey) => ["network", "metrics", filterKey(filter)] as const,
  league: (filter: NetworkFilterKey, metric: string) =>
    ["network", "league", filterKey(filter), metric] as const,
  series: (filter: NetworkFilterKey, metric: string) =>
    ["network", "series", filterKey(filter), metric] as const,
  demand: (from: string, to: string, country: string) =>
    ["network", "demand", from, to, country] as const,
  health: (country: string) => ["network", "health", country] as const,
  reportSubscriptions: () => ["network", "report-subscriptions"] as const,
  messagingProviders: () => ["network", "messaging-providers"] as const,
  messagingCosts: (month: string) => ["network", "messaging-costs", month] as const,
  audit: (filter: AuditFilterKey) =>
    [
      "network",
      "audit",
      filter.country ?? "",
      filter.entity ?? "",
      filter.action ?? "",
      filter.from,
      filter.to,
      filter.cursor ?? "",
    ] as const,
  auditVocabulary: (country: string) => ["network", "audit-vocabulary", country] as const,
  franchises: (country: string) => ["network", "franchises", country] as const,
  services: () => ["network", "services"] as const,
  location: (franchiseId: string) => ["network", "location", franchiseId] as const,
} as const;

interface NetworkFilterKey {
  readonly from: string;
  readonly to: string;
  readonly country: string | null;
  readonly franchiseIds: readonly string[];
  readonly serviceId: string | null;
}

interface AuditFilterKey {
  readonly country: string | null;
  readonly entity: string | null;
  readonly action: string | null;
  readonly from: string;
  readonly to: string;
  readonly cursor: string | null;
}

/** The franchise list is sorted, so selecting the same two locations in a different order
 *  is the same query rather than a second copy of it in the cache. */
function filterKey(filter: NetworkFilterKey): string {
  return [
    filter.from,
    filter.to,
    filter.country ?? "",
    [...filter.franchiseIds].sort().join(","),
    filter.serviceId ?? "",
  ].join("|");
}
